// ========================= src/admin/ProductsTab.jsx =========================
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  collectionGroup,
  getDocs,
  doc,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import { db } from "../../Firebase";
import { ProductsContext } from "../../context/ProductContext";
import { importProducts, manageProduct } from "../../api/productsApi";
import { findExistingProductConflicts, parseProductRows } from "./productImport";
import { calculateManualSavings, normalizeManualProductPrices, normalizeManualVariantPrices } from "./manualProductPrices";
import "./ProductsTab.css";

/* ---------- Helpers ---------- */
const toCsv = (arr) => (Array.isArray(arr) ? arr.join(", ") : "");
const fromCsv = (s) =>
  String(s || "")
    .split(/[\n,]+/)
    .map((x) => x.trim())
    .filter(Boolean);
const currencyCO = (n) =>
  typeof n === "number" ? `$${n.toLocaleString("es-CO")}` : `$${Number(n || 0).toLocaleString("es-CO")}`;
const selectZeroOnFocus = (event) => {
  if (event.currentTarget.value === "0") event.currentTarget.select();
};
const emptyProduct = (over = {}) => ({
  sku: "",
  name: "",
  brand: "",
  department: "",
  category: "",
  subcategory: "",
  description: "",
  price_cop: "",
  oldPrice: "",
  images: [],
  active: true,
  variants: [], // [{color, images:[], tallas:[{size, stock}]}]
  weight_grams: "",
  warranty: "",
  care_instructions: "",
  materials: "",
  specifications: "", // NUEVO
  ...over,
});

/* ---------- Normalizadores ---------- */
const toStr = (v) => (v === undefined || v === null ? "" : String(v).trim());
const sanitizeId = (s) =>
  toStr(s).normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^\w\s-]/g, "").replace(/\s+/g, "_").toLowerCase().slice(0, 120);

/* ---------- Modal (no cierra por clic fuera) ---------- */
function Modal({ open, title, onClose, children, footer }) {
  if (!open) return null;
  return (
    <div className="ap-modal-backdrop" onClick={() => {}}>
      <div className="ap-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        <div className="ap-modal-header">
          <h3>{title}</h3>
          <button className="ap-icon-btn" onClick={onClose} title="Cerrar">✕</button>
        </div>
        <div className="ap-modal-body">{children}</div>
        {footer && <div className="ap-modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

/* ---------- Variants Editor (con imágenes por color) ---------- */
function VariantsEditor({ value = [], onChange }) {
  const setAll = (v) => onChange(Array.isArray(v) ? v : []);
  const addColor = () => setAll([...value, { color: "", images: [], tallas: [{ size: "", price_cop: "", oldPrice: "", stock: "" }] }]);
  const removeColor = (i) => setAll(value.filter((_, idx) => idx !== i));
  const setColor = (i, color) => {
    const next = [...value];
    next[i] = { ...next[i], color };
    setAll(next);
  };
  const addSize = (i) => {
    const next = [...value];
    const tallas = Array.isArray(next[i].tallas) ? next[i].tallas : [];
    next[i] = { ...next[i], tallas: [...tallas, { size: "", price_cop: "", oldPrice: "", stock: "" }] };
    setAll(next);
  };
  const setSizeRow = (i, j, field, v) => {
    const next = [...value];
    const tallas = Array.isArray(next[i].tallas) ? [...next[i].tallas] : [];
    const row = { ...(tallas[j] || { size: "", price_cop: "", oldPrice: "", stock: "" }), [field]: v };
    tallas[j] = row;
    next[i] = { ...next[i], tallas };
    setAll(next);
  };
  const removeSizeRow = (i, j) => {
    const next = [...value];
    const tallas = (next[i].tallas || []).filter((_, idx) => idx !== j);
    next[i] = { ...next[i], tallas: tallas.length ? tallas : [{ size: "", price_cop: "", oldPrice: "", stock: "" }] };
    setAll(next);
  };

  const setVariantImages = (i, src) => {
    const next = [...value];
    const arr = fromCsv(src);
    next[i] = { ...next[i], images: arr };
    setAll(next);
  };
  const appendVariantImage = (i) => {
    const url = window.prompt("Pega la URL de la imagen para este color:");
    if (!url) return;
    const next = [...value];
    const arr = Array.isArray(next[i].images) ? next[i].images : [];
    next[i] = { ...next[i], images: [...arr, String(url).trim()].filter(Boolean) };
    setAll(next);
  };
  const clearVariantImages = (i) => {
    const next = [...value];
    next[i] = { ...next[i], images: [] };
    setAll(next);
  };

  return (
    <div className="var-wrap">
      <div className="var-head">
        <h4>Variantes (Color / Talla / Precios / Stock)</h4>
        <button type="button" className="ap-btn ap-btn--primary" onClick={addColor}>+ Color</button>
      </div>

      {!value.length ? (
        <div className="ap-empty">Sin variantes. Agrega un color.</div>
      ) : (
        value.map((v, i) => (
          <div className="var-card" key={`color-${i}`}>
            <div className="var-row">
              <label className="var-color">
                Color
                <input value={v.color || ""} onChange={(e) => setColor(i, e.target.value)} placeholder="Negro, Azul, Rojo…" />
              </label>

              <button type="button" className="ap-btn ap-btn--danger var-remove" onClick={() => removeColor(i)} title="Eliminar color">
                Eliminar color
              </button>
            </div>

            {/* Imágenes por variación */}
            <div className="span-2" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontWeight: 700 }}>Imágenes de este color (CSV o una por línea)</label>
              <div style={{ display: "flex", gap: 8 }}>
                <textarea
                  rows={2}
                  style={{ flex: 1 }}
                  value={toCsv(v.images || [])}
                  onChange={(e) => setVariantImages(i, e.target.value)}
                  placeholder={`https://...,\nhttps://...`}
                />
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <button type="button" className="ap-btn" onClick={() => appendVariantImage(i)}>+ Añadir</button>
                  <button type="button" className="ap-btn ap-btn--ghost" onClick={() => clearVariantImages(i)}>Vaciar</button>
                  <small style={{ opacity: 0.8, textAlign: "center" }}>
                    {(v.images && v.images.length) || 0} img
                  </small>
                </div>
              </div>
            </div>

            <div className="size-table">
              <div className="size-head">
                <div>Talla</div>
                <div>SKU Maestro asociado</div>
                <div>Precio actual</div>
                <div>Precio anterior</div>
                <div>Stock</div>
                <div>Acción</div>
              </div>
              <div className="size-body">
                {(v.tallas || [{ size: "", price_cop: "", oldPrice: "", stock: "" }]).map((s, j) => (
                  <div className="size-row" key={`size-${i}-${j}`}>
                    <input className="size-input" placeholder="S / 30 / Única…" value={s.size || ""} onChange={(e) => setSizeRow(i, j, "size", e.target.value)} />
                    <input
                      className="master-sku-input mono"
                      value={s.sku_master || "Sin SKU Maestro"}
                      readOnly
                      title={s.sku_master || "Esta variante no tiene un SKU Maestro asociado"}
                      aria-label={`SKU Maestro de ${v.color || "color sin nombre"}, talla ${s.size || "sin talla"}`}
                    />
                    <input type="number" min="1" step="1" className="size-price-input" value={s.price_cop ?? ""} onFocus={selectZeroOnFocus} onChange={(e) => setSizeRow(i, j, "price_cop", e.target.value)} placeholder="Usar general" aria-label={`Precio actual de ${v.color || "color"}, talla ${s.size || "sin talla"}`} />
                    <input type="number" min="1" step="1" className="size-price-input" value={s.oldPrice ?? ""} onFocus={selectZeroOnFocus} onChange={(e) => setSizeRow(i, j, "oldPrice", e.target.value)} placeholder="Opcional" aria-label={`Precio anterior de ${v.color || "color"}, talla ${s.size || "sin talla"}`} />
                    <input type="number" min="0" step="1" className="stock-input" value={s.stock ?? ""} onFocus={selectZeroOnFocus} onChange={(e) => setSizeRow(i, j, "stock", e.target.value)} placeholder="0" />
                    <button type="button" className="ap-btn ap-btn--ghost" onClick={() => removeSizeRow(i, j)}>Quitar</button>
                  </div>
                ))}
              </div>
            </div>

            <div className="var-actions">
              <button type="button" className="ap-btn" onClick={() => addSize(i)}>+ Añadir talla</button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/* ---------- Editor principal ---------- */
function ProductForm({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const savingsPreview = calculateManualSavings(value.price_cop, value.oldPrice);
  const normalizedCategory = String(value.category || "").trim().toLowerCase();
  const damaCategory = normalizedCategory === "dama";
  const hombreCategory = normalizedCategory === "hombre";
  const departmentList = damaCategory ? "dama-departments" : hombreCategory ? "hombre-departments" : undefined;

  return (
    <>
      <div className="ap-form-grid">
        <label>
          SKU
          <input value={value.sku || ""} onChange={(e) => set("sku", e.target.value)} placeholder="JNS210" required />
        </label>
        <label className="span-2">
          Nombre
          <input value={value.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Jean slim fit hombre" required />
        </label>
        <label>
          Marca
          <input value={value.brand || ""} onChange={(e) => set("brand", e.target.value)} />
        </label>
        <label>
          Departamento
          <input list={departmentList} value={value.department || ""} onChange={(e) => set("department", e.target.value)} placeholder={damaCategory ? "Jeans / Ropa deportiva / Blusas…" : hombreCategory ? "Jeans / Camisetas / Ropa deportiva…" : "Hombre / Infantil…"} />
          {damaCategory && <datalist id="dama-departments"><option value="Jeans" /><option value="Ropa deportiva" /><option value="Blusas" /><option value="Ropa interior" /><option value="Accesorios" /><option value="Otros" /></datalist>}
          {hombreCategory && <datalist id="hombre-departments"><option value="Jeans" /><option value="Camisetas" /><option value="Ropa deportiva" /><option value="Ropa interior" /><option value="Accesorios" /><option value="Otros" /></datalist>}
        </label>
        <label>
          Categoría
          <input list="main-product-categories" value={value.category || ""} onChange={(e) => set("category", e.target.value)} placeholder="Moda / Dama / Bolsos…" />
          <datalist id="main-product-categories"><option value="Moda" /><option value="Dama" /><option value="Hombre" /><option value="Bolsos" /><option value="Tecnología" /><option value="Hogar" /></datalist>
        </label>
        <label>
          Subcategoría
          <input value={value.subcategory || ""} onChange={(e) => set("subcategory", e.target.value)} placeholder="Jeans, Camisetas…" />
        </label>
        <label>
          Precio actual general (respaldo)
          <input type="number" min="1" step="1" value={value.price_cop ?? ""} onFocus={selectZeroOnFocus} onChange={(e) => set("price_cop", e.target.value)} placeholder="Ej. 110000" required />
        </label>
        <label>
          Precio anterior general (opcional)
          <input type="number" min="1" step="1" value={value.oldPrice ?? ""} onFocus={selectZeroOnFocus} onChange={(e) => set("oldPrice", e.target.value)} placeholder="Déjalo vacío si no aplica" />
        </label>

        <small className="manual-price-help span-2">Estos valores mantienen compatibilidad con productos antiguos y se usan cuando una talla no tiene precio propio.</small>

        <div className="manual-price-preview span-2" aria-live="polite">
          <strong>Vista previa del precio</strong>
          {Number(value.price_cop) > 0
            ? <span>Precio actual: {currencyCO(Number(value.price_cop))}</span>
            : <span>Ingresa el precio actual para ver la vista previa.</span>}
          {savingsPreview && (
            <>
              <span>Precio anterior: <s>{currencyCO(savingsPreview.oldPrice)}</s></span>
              <span className="manual-price-saving">Ahorras: {currencyCO(savingsPreview.savings)} ({savingsPreview.percentage}%)</span>
            </>
          )}
        </div>

        {/* NUEVOS CAMPOS */}
        <label>
          Peso (Gr)
          <input type="number" min="0" step="1" value={value.weight_grams ?? ""} onFocus={selectZeroOnFocus} onChange={(e) => set("weight_grams", e.target.value)} placeholder="0" />
        </label>
        <label>
          Garantía
          <input value={value.warranty || ""} onChange={(e) => set("warranty", e.target.value)} placeholder="30 días por defectos de fábrica" />
        </label>
        <label className="span-2">
          Cuidados y lavado
          <input value={value.care_instructions || ""} onChange={(e) => set("care_instructions", e.target.value)} placeholder="Lavar a mano, no usar cloro…" />
        </label>
        <label className="span-2">
          Materiales y composición
          <input value={value.materials || ""} onChange={(e) => set("materials", e.target.value)} placeholder="Algodón, poliéster…" />
        </label>

        {/* ✅ Campo ESPECIFICACIONES */}
        <label className="span-2">
          Especificaciones
          <textarea
            rows={3}
            value={value.specifications || ""}
            onChange={(e) => set("specifications", e.target.value)}
            placeholder="Ej.: Corte slim, tiro medio, cierre botón/cremallera, país de origen…"
          />
        </label>

        {/* Imágenes generales del producto */}
        <label className="span-2">
          Imágenes (CSV o una por línea)
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <textarea
              rows={3}
              style={{ flex: 1 }}
              value={toCsv(value.images)}
              onChange={(e) => set("images", fromCsv(e.target.value))}
              placeholder={`https://...,\nhttps://...\n(o una por línea)`}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button
                type="button"
                className="ap-btn"
                onClick={() => {
                  const url = window.prompt("Pega la URL de la imagen:");
                  if (!url) return;
                  const trimmed = String(url).trim();
                  if (!trimmed) return;
                  const next = Array.isArray(value.images) ? [...value.images, trimmed] : [trimmed];
                  set("images", next);
                }}
                title="Añadir una URL sin escribir el CSV"
              >
                + Añadir imagen
              </button>
              <small style={{ opacity: 0.8, textAlign: "center" }}>
                {Array.isArray(value.images) ? value.images.length : 0} img
              </small>
            </div>
          </div>
        </label>

        <label className="span-2">
          Descripción
          <textarea rows={3} value={value.description || ""} onChange={(e) => set("description", e.target.value)} />
        </label>
        <label className="span-2">
  Estado del producto

  <select
    value={value.active ? "activo" : "inactivo"}
    onChange={(e) =>
      set("active", e.target.value === "activo")
    }
  >
    <option value="activo">🟢 Activo (visible)</option>
    <option value="inactivo">🔴 Inactivo (oculto)</option>
  </select>


  <small
    style={{
      display: "block",
      marginTop: 6,
      fontWeight: 600,
      color: value.active ? "#0f9d58" : "#d93025",
    }}
  >
    {value.active ? "🟢 Visible en la tienda" : "🔴 Oculto en la tienda"}
  </small>
</label>
      </div>

      <VariantsEditor value={value.variants || []} onChange={(v) => set("variants", v)} />
    </>
  );
}

/* ---------- Modal: Carga por lotes (sin cambios funcionales) ---------- */
function BatchUploadModal({ open, onClose, onMergeRows, existingProducts }) {
  const [parsed, setParsed] = useState([]);
  const [rawCount, setRawCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [log, setLog] = useState([]);
  const [confirmedExisting, setConfirmedExisting] = useState(false);
  const appendLog = (m) => setLog((prev) => [...prev, m]);
  const conflicts = useMemo(
    () => findExistingProductConflicts(parsed, existingProducts),
    [parsed, existingProducts],
  );

  const handleFile = async (file) => {
    if (!file) return;
    setLoading(true);
    setParsed([]);
    setLog([]);
    setConfirmedExisting(false);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.SheetNames[0];
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheet], { defval: "" });
      setRawCount(rows.length);

      const result = parseProductRows(rows);
      result.errors.forEach((error) => appendLog(`❌ Fila ${error.row} · ${error.field}: ${error.message}`));
      setParsed(result.products);
      if (!result.errors.length) appendLog(`✅ Leídas ${rows.length} filas → ${result.products.length} productos por referencia base.`);
      else appendLog(`Se encontraron ${result.errors.length} errores. No se habilitará la carga hasta corregirlos.`);
    } catch (err) {
      console.error(err);
      appendLog(`❌ Error leyendo archivo: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const uploadAll = async () => {
    if (!parsed.length) return;
    if (conflicts.length && !confirmedExisting) return;
    setLoading(true);
    try {
      const result = await importProducts(
        parsed,
        confirmedExisting ? conflicts.map((conflict) => conflict.sku) : [],
      );
      appendLog(`✅ Importación terminada: ${result.created} creados y ${result.updated} actualizados.`);
      onMergeRows(parsed);
      setLoading(false);
    } catch (err) {
      console.error(err);
      appendLog(`❌ No se pudo importar: ${err.message}`);
      setLoading(false);
    }
  };

  if (!open) return null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Carga por lotes (Excel)"
      footer={
        <>
          <button className="ap-btn" onClick={onClose} disabled={loading}>Cerrar</button>
          <button className="ap-btn ap-btn--primary" onClick={uploadAll} disabled={!parsed.length || loading || (conflicts.length > 0 && !confirmedExisting)}
            title={!parsed.length ? "Sube un archivo primero" : conflicts.length && !confirmedExisting ? "Confirma las referencias existentes" : "Subir a Firestore"}>
            {loading ? "Procesando…" : `Subir ${parsed.length} productos`}
          </button>
        </>
      }
    >
      <div className="ap-form-grid">
        <label className="span-2">
          Archivo (.xlsx, .xls, .csv)
          <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => handleFile(e.target.files?.[0])} disabled={loading} />
        </label>

        <div className="ap-hint">
          Columnas esperadas: <strong>COD Ref SKU, Nombre, Marca, Categoría, Sub-Categoría, Departamento, Descripción, Materiales y composición, Cuidados y lavado, Garantía, Precio actual, Precio anterior, Peso (Gr), Imagen Principal, Imagen1, Imagen2, Color, Talla, Cantidad, Imagen Color 1, Imagen Color 2, Actualizar inventario</strong>. Precio actual queda en K, Precio anterior en L y Peso (Gr) en M. El <strong>SKU Maestro se genera automáticamente</strong> para cada variación; si tu archivo ya contiene esa columna, consérvala. Los archivos antiguos con Precio (COL) siguen siendo compatibles; la nueva plantilla no necesita esa columna. Precio anterior es opcional y cada fila puede tener precios distintos según su combinación de color y talla.
        </div>

        <div className="ap-summary">
          {rawCount ? `Filas leídas: ${rawCount}. ` : ""}{parsed.length ? `Productos únicos: ${parsed.length}.` : ""}
        </div>

        {conflicts.length > 0 && (
          <div className="ap-import-warning span-2" role="alert">
            <strong>⚠️ {conflicts.length} referencia{conflicts.length === 1 ? " ya existe" : "s ya existen"}</strong>
            <p>Estos códigos actualizarán productos existentes; no crearán productos nuevos.</p>
            <div className="ap-import-conflicts">
              {conflicts.map((conflict) => (
                <div key={conflict.sku} className="ap-import-conflict">
                  <span className="mono">{conflict.sku}</span>
                  <span>Actual: <strong>{conflict.existingName}</strong></span>
                  <span>Excel: <strong>{conflict.incomingName}</strong></span>
                </div>
              ))}
            </div>
            <label className="ap-import-confirm">
              <input
                type="checkbox"
                checked={confirmedExisting}
                onChange={(event) => setConfirmedExisting(event.target.checked)}
              />
              Confirmo que deseo actualizar estos productos existentes.
            </label>
          </div>
        )}

        {parsed.length > 0 && (
          <div className="ap-table preview">
            <div className="ap-thead">
              <div>SKU</div><div>Nombre</div><div>Categoría</div><div>Precio</div><div>Variantes</div><div>Imágenes</div>
            </div>
            <div className="ap-tbody">
              {parsed.slice(0, 20).map((p) => (
                <div key={p.sku} className="ap-row">
                  <div className="ap-cell mono">{p.sku}</div>
                  <div className="ap-cell">{p.name}</div>
                  <div className="ap-cell">{p.category || "—"}</div>
                  <div className="ap-cell">{currencyCO(p.price_cop)}</div>
                  <div className="ap-cell">{p.variants?.map((v) => `${v.color}(${v.tallas?.length || 0})`).join(", ")}</div>
                  <div className="ap-cell">{p.images?.length || 0}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="ap-log">{log.map((l, i) => (<div key={i} className="ap-log-line">{l}</div>))}</div>
      </div>
    </Modal>
  );
}

/* ---------- Main ---------- */
export default function AdminProducts() {
  const { refresh: refreshProducts } = useContext(ProductsContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyProduct());
  const [creating, setCreating] = useState(false);
  const [catForCreate, setCatForCreate] = useState("ropa");

  const [batchOpen, setBatchOpen] = useState(false);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collectionGroup(db, "items"));
      const items = snap.docs.map((d) => ({ id: d.id, ref: d.ref, catSlug: d.ref.parent?.parent?.id || "sin_categoria", ...d.data() }));
      items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setRows(items);
    } catch (e) {
      console.error("❌ Error listando productos:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((p) => {
      const txt = [p.name, p.sku, p.brand, p.department, p.category, p.subcategory].filter(Boolean).join(" ").toLowerCase();
      return txt.includes(needle);
    });
  }, [rows, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  const onOpenEdit = (row) => {
    setEditing(row);
    setDraft(
      emptyProduct({
        ...row,
        active: row.active !== false,
        images: Array.isArray(row.images) ? row.images : fromCsv(row.images),
        variants: Array.isArray(row.variants)
          ? row.variants.map((v) => ({
              color: v.color || "",
              images: Array.isArray(v.images) ? v.images : fromCsv(v.images || ""),
              tallas: Array.isArray(v.tallas) ? v.tallas : [],
            }))
          : [],
        weight_grams: Number(row.weight_grams || 0),
        warranty: row.warranty || "",
        care_instructions: row.care_instructions || "",
        materials: row.materials || "",
        specifications: row.specifications || "",
        oldPrice: row.oldPrice ?? "",
      })
    );
  };
  const onCloseModal = () => {
    setEditing(null);
    setCreating(false);
    setDraft(emptyProduct());
  };

  const onSave = async () => {
    try {
      if (!toStr(draft.sku)) return alert("El SKU es obligatorio.");
      if (!toStr(draft.name)) return alert("El nombre es obligatorio.");
      let prices;
      try {
        prices = normalizeManualProductPrices(draft.price_cop, draft.oldPrice);
      } catch (priceError) {
        return alert(priceError.message);
      }

      try {
        (draft.variants || []).forEach((variant) => {
          (variant.tallas || []).forEach((size) => normalizeManualVariantPrices(size.price_cop, size.oldPrice));
        });
      } catch (variantPriceError) {
        return alert(variantPriceError.message);
      }

      const variantsClean = (draft.variants || [])
        .map((v) => ({
          color: String(v.color || "").trim(),
          images: (v.images || []).map((u) => toStr(u)).filter(Boolean),
          tallas: (v.tallas || [])
            .map((t) => ({
              size: String(t.size || "").trim(),
              stock: Math.max(0, Number(t.stock || 0)),
              ...normalizeManualVariantPrices(t.price_cop, t.oldPrice),
              ...(t.sku_master ? { sku_master: toStr(t.sku_master) } : {}),
            }))
            .filter((t) => t.size),
        }))
        .filter((v) => v.color && (v.tallas.length || v.images.length));

      const payload = {
        sku: toStr(draft.sku) || editing?.sku || "",
        name: toStr(draft.name),
        brand: toStr(draft.brand),
        department: toStr(draft.department),
        category: toStr(draft.category),
        subcategory: toStr(draft.subcategory),
        description: toStr(draft.description),
        price_cop: prices.price_cop,
        oldPrice: prices.oldPrice,
        images: draft.images || [],
        active: draft.active !== false,
        variants: variantsClean,
        weight_grams: Number(draft.weight_grams || 0),
        warranty: toStr(draft.warranty),
        care_instructions: toStr(draft.care_instructions),
        materials: toStr(draft.materials),
        specifications: toStr(draft.specifications), // NUEVO
        updated_at: new Date(),
      };

      if (creating) {
        const categoriaId = sanitizeId(payload.category || "sin_categoria");
        const result = await manageProduct("save", { product: payload });
        const ref = doc(db, result.path || `productos/${categoriaId}/items/${payload.sku}`);
        setRows((prev) => [...prev, { id: payload.sku, ref, catSlug: categoriaId, ...payload }]);
      } else if (editing?.ref) {
        const result = await manageProduct("save", { path: editing.ref.path, product: payload });
        const categoriaId = sanitizeId(payload.category || "sin_categoria");
        const ref = doc(db, result.path || `productos/${categoriaId}/items/${payload.sku}`);
        setRows((prev) => prev.map((r) => (
          r.id === editing.id && r.catSlug === editing.catSlug
            ? { ...editing, ...payload, ref, catSlug: categoriaId }
            : r
        )));
      }
      await refreshProducts();
      onCloseModal();
    } catch (e) {
      console.error("❌ Error guardando producto:", e);
      alert("No se pudo guardar. Revisa la consola.");
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Eliminar "${row.name}"?`)) return;
    try {
      await manageProduct("delete", { path: row.ref.path });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      await refreshProducts();
    } catch (e) {
      console.error("❌ Error eliminando:", e);
      alert("No se pudo eliminar.");
    }
  };

  // 👇 NUEVO: activar/desactivar producto directamente desde la tabla
  const onToggleActive = async (row) => {
    const nextActive = !(row.active !== false);
    const verb = nextActive ? "activar" : "desactivar";
    if (!window.confirm(`¿Seguro que deseas ${verb} "${row.name}"?`)) return;
    try {
      await manageProduct("toggle", { path: row.ref.path, active: nextActive });
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id && r.catSlug === row.catSlug ? { ...r, active: nextActive } : r
        )
      );
      await refreshProducts();
    } catch (e) {
      console.error("❌ Error actualizando estado:", e);
      alert("No se pudo actualizar el estado del producto.");
    }
  };

  return (
    <div className="ap-wrap">
      <header className="ap-header">
        <h2>Productos</h2>
        <div className="ap-controls">
          <input
            className="ap-search"
            placeholder="Buscar por nombre, SKU, marca, categoría…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
          />
          <button className="ap-btn" onClick={() => setBatchOpen(true)} title="Cargar productos desde Excel">
            Añadir por lotes
          </button>
          <button className="ap-btn ap-btn--primary" onClick={() => { setCreating(true); setDraft(emptyProduct()); }}>
            + Nuevo
          </button>
        </div>
      </header>

      {loading ? (
        <div className="ap-empty">Cargando…</div>
      ) : !rows.length ? (
        <div className="ap-empty">No hay productos.</div>
      ) : (
        <>
          <div className="ap-table">
            <div className="ap-thead">
              <div>Producto</div><div>SKU</div><div>Categoría</div><div>Precio</div><div>Activo</div><div>Acciones</div>
            </div>
            <div className="ap-tbody">
              {pageRows.map((r) => (
                <div className="ap-row" key={`${r.catSlug}-${r.id}`}>
                  <div className="ap-cell">
                    <div className="ap-name">{r.name || "—"}</div>
                    <div className="ap-sub">
                      {r.brand || "—"} · {r.department || "—"} · {r.category || "—"}{r.subcategory ? ` / ${r.subcategory}` : ""}
                    </div>
                  </div>
                  <div className="ap-cell mono">{r.sku || "—"}</div>
                  <div className="ap-cell">{r.category || r.catSlug || "—"}</div>
                  <div className="ap-cell">{currencyCO(r.price_cop)}</div>
                  <div className="ap-cell">
                    <span className={`ap-badge ${r.active ? "ok" : "off"}`}>{r.active ? "Sí" : "No"}</span>
                  </div>
                  <div className="ap-cell actions">
                    <button className="ap-btn ap-btn--ghost" onClick={() => onOpenEdit(r)}>Editar</button>

                    {/* 👇 NUEVO botón activar/desactivar */}
                    <button
                      className={`ap-btn ${r.active !== false ? "ap-btn--warning" : "ap-btn--success"}`}
                      onClick={() => onToggleActive(r)}
                      title={r.active !== false ? "Ocultar producto en la tienda" : "Mostrar producto en la tienda"}
                    >
                      {r.active !== false ? "Desactivar" : "Activar"}
                    </button>

                    <button className="ap-btn ap-btn--danger" onClick={() => onDelete(r)}>Eliminar</button>
                    <a
                      className="ap-btn ap-btn--link"
                      href={`/products/${encodeURIComponent(r.catSlug)}/${encodeURIComponent(r.id)}?adminView=${encodeURIComponent(r.updated_at?.seconds || r.updated_at?._seconds || "current")}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Ver
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="ap-pager">
            <button className="ap-btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>← Anterior</button>
            <span>Página {page} de {totalPages}</span>
            <button className="ap-btn" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Siguiente →</button>
          </div>
        </>
      )}

      <BatchUploadModal
        open={batchOpen}
        onClose={() => setBatchOpen(false)}
        onMergeRows={() => { setBatchOpen(false); loadProducts(); refreshProducts(); }}
        existingProducts={rows}
      />

      <Modal
        open={!!editing || creating}
        title={creating ? "Nuevo producto" : "Editar producto"}
        onClose={onCloseModal}
        footer={
          <>
            <button className="ap-btn" onClick={onCloseModal}>Cancelar</button>
            {creating && (
              <div className="ap-cat-select">
                <span>Guardar en:</span>
                <select value={catForCreate} onChange={(e) => setCatForCreate(e.target.value)} title="ID doc categoría (slug)">
                  <option value="ropa">ropa</option>
                  <option value="calzado">calzado</option>
                  <option value="accesorios">accesorios</option>
                  <option value="sin_categoria">sin_categoria</option>
                </select>
              </div>
            )}
            <button className="ap-btn ap-btn--primary" onClick={onSave}>Guardar</button>
          </>
        }
      >
        <ProductForm value={draft} onChange={setDraft} />
      </Modal>
    </div>
  );
}
